// ============================================================================
// LOCATION SEARCH PICKER COMPONENT
// Search ALL locations across all contacts, auto-populates contact when selected
// Date: December 9, 2025
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import type { SingleValue, StylesConfig } from 'react-select';
import { authService } from '@/lib/authService';

function debounce<TArgs extends unknown[]>(callback: (...args: TArgs) => void, wait = 0) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const debounced = (...args: TArgs) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      callback(...args);
    }, wait);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  return debounced;
}

// ============================================================================
// TYPES
// ============================================================================

interface Location {
  id: number;
  contact_id: number;
  location_name: string;
  location_type: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  is_primary: boolean;
  is_billing: boolean;
  is_shipping: boolean;
  // Contact info (joined)
  contact_name?: string;
  contact_company?: string;
}

interface LocationSearchPickerProps {
  value?: number | null;
  onChange: (locationId: number | null, location: Location | null) => void;
  onContactChange?: (contactId: number | null) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}

interface LocationOption {
  value: number;
  label: string;
  location: Location;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function LocationSearchPicker({
  value,
  onChange,
  onContactChange,
  placeholder = 'Search locations by name, address, or customer...',
  className = '',
  required = false,
  disabled = false
}: LocationSearchPickerProps) {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load a specific location by ID (for pre-selected values)
  const loadLocationById = async (locationId: number): Promise<Location | null> => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return null;
      
      const response = await fetch(`/api/v1/finance/locations/${locationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success && data.data) {
        return data.data as Location;
      }
    } catch (error) {
      console.error('Error loading location by ID:', error);
    }
    return null;
  };

  // Load selected location on mount if value is provided
  useEffect(() => {
    const initSelectedLocation = async () => {
      if (value && value !== selectedLocation?.id) {
        setIsLoading(true);
        const location = await loadLocationById(value);
        if (location) {
          setSelectedLocation(location);
        }
        setIsLoading(false);
      }
    };
    initSelectedLocation();
  }, [value, selectedLocation?.id]);

  // Format address for display
  const formatAddress = useCallback((location: Location): string => {
    const parts = [];
    if (location.address_line1) parts.push(location.address_line1);
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    return parts.length > 0 ? parts.join(', ') : '';
  }, []);

  // Format label with contact name
  const formatLabel = useCallback((location: Location): string => {
    let label = location.location_name;
    const address = formatAddress(location);
    if (address) {
      label += ` - ${address}`;
    }
    // Add contact info
    const contactName = location.contact_name || location.contact_company;
    if (contactName) {
      label += ` (${contactName})`;
    }
    if (location.is_primary) {
      label += ' ⭐';
    }
    return label;
  }, [formatAddress]);

  // Search locations API
  const searchLocations = useCallback(async (inputValue: string): Promise<LocationOption[]> => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return [];

      const url = new URL('/api/v1/contacts/locations/search', window.location.origin);
      if (inputValue) {
        url.searchParams.set('search', inputValue);
      }
      url.searchParams.set('limit', '50');

      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success && data.data) {
        return data.data.map((loc: Location) => ({
          value: loc.id,
          label: formatLabel(loc),
          location: loc
        }));
      }
      return [];
    } catch (error) {
      console.error('Error searching locations:', error);
      return [];
    }
  }, [formatLabel]);

  // Debounced search
  const loadOptions = useCallback(
    (inputValue: string, callback: (options: readonly LocationOption[]) => void) => {
      debounce((val: string, cb: (opts: readonly LocationOption[]) => void) => {
        searchLocations(val).then(cb);
      }, 300)(inputValue, callback);
    },
    [searchLocations]
  );

  // Get current selected option
  const getSelectedOption = () => {
    if (!selectedLocation) return null;
    return {
      value: selectedLocation.id,
      label: formatLabel(selectedLocation),
      location: selectedLocation
    };
  };

  // Handle selection change
  const handleChange = (option: SingleValue<LocationOption>) => {
    const location = option?.location || null;
    setSelectedLocation(location);
    onChange(option?.value || null, location);
    
    // Auto-populate contact if callback provided
    if (onContactChange && location?.contact_id) {
      onContactChange(location.contact_id);
    }
  };

  // Custom styles for react-select
  const customStyles: StylesConfig<LocationOption, false> = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
      },
      backgroundColor: disabled ? '#f3f4f6' : 'white'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
        ? '#eff6ff' 
        : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      '&:active': {
        backgroundColor: '#3b82f6'
      },
      padding: '10px 12px'
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  // Custom format for option display
  const formatOptionLabel = (option: LocationOption) => {
    const loc = option.location;
    if (!loc) return option.label;
    
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <span className="font-medium">{loc.location_name}</span>
          {loc.is_primary && <span className="text-yellow-500">⭐</span>}
        </div>
        {(loc.address_line1 || loc.city) && (
          <span className="text-sm text-gray-500">
            {[loc.address_line1, loc.city, loc.state].filter(Boolean).join(', ')}
          </span>
        )}
        {(loc.contact_name || loc.contact_company) && (
          <span className="text-xs text-blue-600">
            {loc.contact_name || loc.contact_company}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={className}>
      <AsyncSelect<LocationOption, false>
        value={getSelectedOption()}
        onChange={handleChange}
        loadOptions={loadOptions}
        defaultOptions
        cacheOptions
        isLoading={isLoading}
        placeholder={placeholder}
        isClearable={!required}
        isSearchable
        isDisabled={disabled}
        styles={customStyles}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={({ inputValue }) => 
          inputValue ? 'No locations found' : 'Start typing to search locations...'
        }
        loadingMessage={() => 'Searching locations...'}
      />
    </div>
  );
}
